/**
 * The page-title registry behind the top bar's title.
 *
 * Every mounted page title (a `PageHeader`, or a screen calling
 * `usePageTitle`) registers `{ id, depth, seq, titleText }`. Only plain data
 * lives here — the title node itself stays in the page's own React tree and is
 * portalled into the bar by whichever registration wins.
 *
 * Winner: the deepest route (`depth` = number of parent route matches), then
 * the latest registration (`seq`). A settings layout's "Settings" loses to the
 * leaf screen's own title whichever of the two mounted first.
 */

export interface TitleEntry {
  id: string;
  depth: number;
  seq: number;
  /** Plain-text title for `document.title`; undefined when the title is a node. */
  titleText: string | undefined;
}

export interface TitleStore {
  set: (entry: TitleEntry) => void;
  /** Change the text of a registration without touching its order. */
  setText: (id: string, titleText: string | undefined) => void;
  remove: (id: string) => void;
  subscribe: (listener: () => void) => () => void;
  getWinner: () => TitleEntry | null;
}

export function pickWinner(entries: Iterable<TitleEntry>): TitleEntry | null {
  let best: TitleEntry | null = null;
  for (const e of entries) {
    if (best === null || e.depth > best.depth || (e.depth === best.depth && e.seq > best.seq)) {
      best = e;
    }
  }
  return best;
}

let seqCounter = 0;
/** Registration order — taken each time a title (re-)registers. */
export function nextTitleSeq(): number {
  seqCounter += 1;
  return seqCounter;
}

export function createTitleStore(): TitleStore {
  const entries = new Map<string, TitleEntry>();
  const listeners = new Set<() => void>();
  let winner: TitleEntry | null = null;

  const changed = (): void => {
    winner = pickWinner(entries.values());
    for (const l of listeners) l();
  };

  return {
    set: (entry) => {
      entries.set(entry.id, entry);
      changed();
    },
    setText: (id, titleText) => {
      const prev = entries.get(id);
      if (prev === undefined || prev.titleText === titleText) return;
      entries.set(id, { ...prev, titleText });
      changed();
    },
    remove: (id) => {
      if (entries.delete(id)) changed();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    getWinner: () => winner,
  };
}
